import * as React from "react";
import { Alert } from "@mui/material";
import { intersect } from "utils/arrayUtils";
import { useUserSession } from "hooks/useUserSession";
import { useEnabledApplications } from "hooks/useEnabledApplications";
import { useTranslation } from "react-i18next";
import { useNavigation } from "../../hooks/useNavigation";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { signInWithRedirect } from "aws-amplify/auth";
import { LoadingPage } from "../LoadingPage/LoadingPage";
import { CustomOAuthState } from "../../types/CustomOAuthState";
import { ROUTES } from "../../utils/navigationUtils";
import { setInitialPagePath } from "../../store/userSession/userSessionActions";

interface Props {
  allowedRoles: string[];
  requiredApplications?: string[];
  children: React.ReactNode;
}

const RestrictedPage: React.FC<Props> = ({ allowedRoles, requiredApplications, children }) => {
  const { t } = useTranslation();
  const { navigateTo } = useNavigation();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { username, roles = [], initialPagePath } = useUserSession();
  const { data: enabledApplications, isLoading: isEnabledApplicationsLoading } = useEnabledApplications();

  const roleSatisfied = React.useMemo(() => intersect(roles, allowedRoles), [roles, allowedRoles]);
  const applicationAccessSatisfied = React.useMemo(() => {
    if (!enabledApplications?.length || !requiredApplications?.length) {
      return true;
    }
    return intersect(enabledApplications, requiredApplications);
  }, [requiredApplications, enabledApplications]);

  React.useEffect(() => {
    if (username && initialPagePath === pathname) {
      dispatch(setInitialPagePath(""));
    }
  }, [username, initialPagePath, pathname, dispatch]);

  React.useEffect(() => {
    if (!username) {
      if (window.location.pathname === "/") {
        navigateTo(ROUTES.HOME);
      } else {
        const customOAuthState: CustomOAuthState = {
          initialPagePath: window.location.pathname
        };
        signInWithRedirect({
          customState: JSON.stringify(customOAuthState)
        });
      }
    }
  }, [username, navigateTo]);

  if (isEnabledApplicationsLoading && requiredApplications && requiredApplications.length > 0) {
    return <LoadingPage />;
  }

  if (!roleSatisfied) {
    return (
      <Alert sx={{ m: 3 }} color="error">
        {t("restrictedPage.accessRestricted")}
      </Alert>
    );
  }

  if (!applicationAccessSatisfied) {
    return (
      <Alert sx={{ m: 3 }} color="error">
        {t("restrictedPage.applicationAccessDisabled")}
      </Alert>
    );
  }

  return <>{children}</>;
};

export { RestrictedPage };
